import React from "react";
import Link from "next/link";
import { cn, truncateText } from "utils/utils";
import { ClerkAvatar } from "modules/common/components/avatar/ClerkAvatar";

interface UserAvatarLinkProps {
  username: string;
  avatar: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  ring?: boolean;
  ringColor?: string;
  maxUsernameLength?: number;
  usernameClassName?: string;
  className?: string;
  /** Compact content rendered inline after the username. */
  usernameAdornment?: React.ReactNode;
  /** Presence dot on the avatar's bottom-right. */
  online?: boolean;
  /** Extra content rendered below the username inside the same link (e.g. a timestamp). */
  children?: React.ReactNode;
}

// Static map — Tailwind JIT needs complete class names.
const ringWidthClasses = {
  xs: "ring-1",
  sm: "ring-1",
  md: "ring-2",
  lg: "ring-2",
  xl: "ring-3",
};

export function UserAvatarLink({
  username,
  avatar,
  size = "md",
  ring = true,
  ringColor,
  maxUsernameLength,
  usernameClassName,
  className,
  usernameAdornment,
  online,
  children,
}: UserAvatarLinkProps) {
  const displayName = maxUsernameLength
    ? truncateText(username, maxUsernameLength)
    : username;

  return (
    <Link
      href={`/users/${username}`}
      className={cn("group/avatar flex min-w-0 items-center gap-2", className)}
      aria-label={
        online
          ? `View ${username}'s profile, Active now`
          : `View ${username}'s profile`
      }
    >
      <ClerkAvatar
        preferredAvatar={avatar}
        username={username}
        size={size}
        ring={ring}
        ringColor={cn(
          // Invisible until hover; explicit ringColor overrides for a constant ring.
          "group-hover/avatar:ring-primary ring-transparent transition-shadow",
          ringColor
        )}
        ringWidth={ringWidthClasses[size]}
        online={online}
        alt={`${username} avatar`}
      />
      <div className="min-w-0">
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={cn(
              "min-w-0 truncate group-hover/avatar:underline",
              usernameClassName
            )}
          >
            {displayName}
          </span>
          {usernameAdornment}
        </span>
        {children}
      </div>
    </Link>
  );
}
